import { VStack } from "@/components/ui/vstack";
import { View } from "@/components/ui/view";
import { Text } from "@/components/ui/text";
import { Progress, ProgressFilledTrack } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Pressable } from "@/components/ui/pressable";
import { Center } from "@/components/ui/center";
import i18n from 'i18n-js';
import React, {useEffect, useRef, useState} from 'react';
import { FlatList } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import Footer from "@/components/basic/Footer";
import ItemText from '@/components/basic/ItemText';
import ImageSynced from '@/components/media/ImageSynced';
import MediaCarousell from "@/components/media/MediaCarousell";
import ListElement from "@/components/workflow/ListElement";
import useRefreshEffect from '@/hooks/refreshed';
import { useIsFocused } from '@react-navigation/native';
import { switchScreen } from '@/hooks/navigate';
import { Platform } from 'react-native';
import shared from "@/assets/images/shareButton.png"
import avatar1 from "@/assets/images/avatar_mit_background.png"
import inaLogo from "@/assets/icons/icon_blue_rounded_512x512.png"
import placeholder from "@/assets/images/placeholder.png"
import { taskStart, taskDone, updateSettings } from "@/stores/mainSlice";
import ModalWaiting from "@/screens/modals/ModalWaiting";
import { backendRequest } from "@/stores/thunks";

const WorkflowHeader = ({ navigation, task, isSimulator }) => {
    const imgLoad = useSelector((state) => state.images.load)
    const contacts = useSelector((state) => state.contacts)
    const user = useSelector((state) => state.user)
    const [contact, setContact] = useState(contacts.active.find(contact => contact.id == task.contact))
    const [avatar, setAvatar] = useState(task?.owned && task?.shared ? shared : task?.simulatorPreview || task?.id.length < 2 ? inaLogo : task?.contact || (user.userImage ? (user.avatar || user.id) : avatar1))

    return (
        <>
            {task.media.length > 0
                ? <MediaCarousell uris={task.media} overlays={task.overlays}/>
                : Platform.OS !== "web" && <View className="mt-8" />
            }
            {!isSimulator && <View className="flex-row justify-end mr-5">
                {avatar &&
                <Pressable className={`${Platform.OS !== "web" ? 'absolute -top-7' : ''} border border-white rounded-full data-[disabled=true]:opacity-60`} onPress={() => task.owned ? task.shared && switchScreen(navigation, 'ShareWorkflow', { task: task, info: task.title, isEdit: false, isFinal: true }) : contact ? switchScreen(navigation, 'Contact', { contact: contact, isEdit: false }) : switchScreen(navigation, 'Publisher', { publisher: task.publisher, title: task.title })}>
                    <ImageSynced key={avatar + '_' + imgLoad[avatar]?.duration} uri={avatar} refresh={imgLoad[avatar]?.duration} sync={'/contact/media/' + avatar} isAvatar={true} className="border border-primary-900 rounded-full" />
                </Pressable>}
            </View>}
            <VStack className="m-3">
                <ItemText title={task.title} text={task.text} isSimulator={isSimulator}/>
                {task.step != -1 &&
                    <Progress value={100 * (task.step + 1) / task.steps.length} className="w-[100%] mt-8 -mb-1 h-2 border bg-primary-50 border-primary-300">
                        <ProgressFilledTrack className="h-2" />
                    </Progress>}
                <Text className="mb-2 mt-1 text-lg leading-lg text-secondary-500">
                    {task.step == -1 ? i18n.t('showWorkflowScreen.numberOfSteps', { steps: task.steps.length }) : i18n.t('showWorkflowScreen.stepProgress', { step: task.step + 1, maxSteps: task.steps.length })}
                </Text>
            </VStack>
        </>
    );
}

/** @type(Workflow) */
const fakeTask = {
    id: 'fakeTaskId',
    title: i18n.t('showWorkflowScreen.fakeTask.title'),
    text: i18n.t('showWorkflowScreen.fakeTask.text'),
    steps: [
        {title:  i18n.t('showWorkflowScreen.fakeTask.stepTitle'), text: i18n.t('showWorkflowScreen.fakeTask.stepText'), type: 0, media: [placeholder]},
        {title:  i18n.t('showWorkflowScreen.fakeTask.stepTitle'), text: i18n.t('showWorkflowScreen.fakeTask.stepText'), type: 0, media: [placeholder]},
    ],
    step: -1,
    media: [placeholder]
}

/**
 * @param {string} route.params.taskid - id of Workflow
 * @param {boolean} route.params.isLoading - indikator if data needs to be loaded
 */
export default function ShowWorkflow2({ route, navigation }) {
    const isOffScreen = !useIsFocused() && Platform.OS !== 'web'
    const { taskid, isLoading, isSimulator } = route.params
    const dispatch = useDispatch()
    /** @type(Workflow) */
    const task = useSelector(state => state.workflows[taskid])
    const icons = useSelector((state) => state.icons)
    const online = useSelector((state) => state.server.online)
    const insets = useSafeAreaInsets();
    const [marked, setMarked] = useState(() => updMarked())
    const changed = useSelector((state) => state.settings.changed || [])
    const restricted = task ? task.step > -1 && task?.settings?.strict : false

    const [showModal, setShowModal] = useState(isLoading)
    const TIMEOUT_SECONDS = 10;
    const timeoutRef = useRef(null);
    const waiting = !task;

    useEffect(() => {
       if (task){
           setShowModal(false)
           clearTimeout(timeoutRef.current)
       } else {
           timeoutRef.current = setTimeout(() => {
               switchScreen(navigation, 'Home')
           }, TIMEOUT_SECONDS * 1000)
           setShowModal(true)
       }
    }, [task])

    useEffect(() => {
        if(!waiting) return;
        dispatch(backendRequest('GET_WORKFLOWS_INLINE', '/contact/user', null, 'GET', false))
    }, [waiting, dispatch])

    function updMarked() {
        if (!task || task.studio) {
            return []
        }
        let mark = 0
        return task.steps.map((step, s) => {
            if (step.type == 3) {
                mark = Math.max(0, Math.max(step.no ?? -1, step.yes ?? -1) - s - 1)
                return false
            }
            return mark-- > 0
        })
    }

    const actions = [
        { icon: 'keyboard-arrow-left', text: 'footer.back', component: 'BACK' },
        { icon: 'home', text: 'footer.home', component: 'Home' },
        { icon: !task?.studio && task?.step == -1 && task?.owned && !(online && task?.locked) ? 'edit' : '', text: 'footer.edit', component: 'WorkflowEdit', params: { taskid: task?.id, title: 'footer.edit' } }
    ]
    useEffect(() => {
        if (changed.includes(taskid)) {
            console.log('UNCHANGE', 'ShowWorkflow')
            dispatch(updateSettings({ settings: { changed: changed.filter(id => id != taskid) } }))
        }
    }, [])

    useEffect(() => {
        if (task) {
            navigation.setOptions({ info: task.title, task: task, owned: task?.owned })
        }else if(isLoading){
            navigation.setOptions({ info: fakeTask.title, task: fakeTask, owned: fakeTask?.owned })
        }

    }, [task])

    useRefreshEffect(() => {
        setMarked(updMarked())
    }, [task?.steps])

    const actionTask = () => {
        if (task.step == -1)
            dispatch(taskStart({ task: task }));
        switchScreen(navigation, 'ShowStep', { taskid: taskid, idx: task.step == -1 ? 0 : task.step, extra: marked, isSimulator })
    }

    const actionStopTask = () => {
        if (task.step != -1)
            dispatch(taskDone({ taskid: task.id, abort: true }))
    }

    const renderItem = ({ item, index }) => {
        // flag marker for (the last step without positive goto and two positive decisions) or a goto to end
        const endStep = task && (index == task.steps.length - 1 && (item.goto ?? -1) < 0 && Math.min(item.no ?? -1, item.yes ?? -1) < 0) || Math.min(item.no ?? -1, item.yes ?? -1) == -2 || item.goto == -2 ? 'flag' : null;
        // console.log('END', index == task.steps.length - 1, (item.goto ?? -1) < 0, Math.min(item.no ?? -1, item.yes ?? -1) < 0, item.goto == -2, item)
        return (
            <Button key={'S' + index} disabled={restricted && index > task.step} onPress={() => switchScreen(navigation, 'ShowStep', { taskid: taskid, idx: index, extra: marked , isSimulator: isSimulator, isPreview: true})} className="px-2 bg-secondary-50 h-[90px] active:bg-primary-300">
                <ListElement idx={index} element={item} avatar={icons.types[item.type]} currentStep={index == task?.item} marked={marked[index]} disabled={restricted && index > task.step} graph={null} icon={endStep} />
            </Button>
        );
    }

    return (isOffScreen ? <></> : <View className="bg-secondary-50 flex-1">
        {isLoading || task ? (
            <FlatList className="bg-secondary-50"
                      contentContainerStyle={{ paddingBottom: 120 + insets.bottom }}
                      data={task ? task.steps: fakeTask.steps}
                      renderItem={renderItem}
                      keyExtractor={(item, index) => item.title + index}
                      initialNumToRender={2}
                      maxToRenderPerBatch={8}
                      windowSize={8}
                      ListHeaderComponent={<WorkflowHeader navigation={navigation} task={task ? task : fakeTask} isSimulator={isSimulator}/>}
            />
        ) : (
            <Center className="mx-5 flex-1">
                <Text bold className="text-2xl leading-2xl text-secondary-900">{i18n.t('showWorkflowScreen.deleted')}</Text>
            </Center>
        )}

        <ModalWaiting showModal={showModal}/>

        <Footer 
            navigation={navigation} 
            actions={actions}
            cancel={task == null || task.steps.length == 0 || restricted || task?.step == -1 ? null : { action: actionStopTask, info: 'showWorkflowScreen.buttonStopTask' }} 
            button2={task == null || task.steps.length == 0 ? null : { action: actionTask, info: task?.step == -1 ? 'showWorkflowScreen.buttonStartTask' : 'showWorkflowScreen.buttonResumeTask' }} 
            hideFooterActions={isSimulator}
            />
    </View>);
}
